import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';

const KEY = 'botanica_kit_v1';

function readLocal() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw).items || [] : [];
  } catch {
    return [];
  }
}
function writeLocal(items) {
  localStorage.setItem(KEY, JSON.stringify({ items }));
}

const KitContext = createContext(null);

export function KitProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(readLocal());

  // Load from backend when authenticated
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (user) {
        try {
          const res = await axios.get(`${API}/kit`);
          if (active) setItems(res.data.items || []);
        } catch (e) {
          /* keep local */
        }
      } else {
        setItems(readLocal());
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [user]);

  const isSaved = useCallback((id) => items.some((i) => i.id === id), [items]);

  const toggle = useCallback(
    (item) => {
      const exists = items.some((i) => i.id === item.id);
      const next = exists ? items.filter((i) => i.id !== item.id) : [...items, item];
      setItems(next);
      if (user) {
        if (exists) {
          axios.delete(`${API}/kit/${encodeURIComponent(item.id)}`).catch(() => {});
        } else {
          axios.post(`${API}/kit`, item).catch(() => {});
        }
      } else {
        writeLocal(next);
      }
      return !exists;
    },
    [items, user]
  );

  const remove = useCallback(
    (id) => {
      const next = items.filter((i) => i.id !== id);
      setItems(next);
      if (user) axios.delete(`${API}/kit/${encodeURIComponent(id)}`).catch(() => {});
      else writeLocal(next);
    },
    [items, user]
  );

  return (
    <KitContext.Provider value={{ items, isSaved, toggle, remove }}>
      {children}
    </KitContext.Provider>
  );
}

export function useKit() {
  return useContext(KitContext);
}
